import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { createSeries, listCreatorSeries, type CreatorSeriesSummary } from '@/lib/api/creator'

interface SeriesRow {
  id: string
  title: string
  visibility: 'public' | 'unlisted' | 'draft'
  episodes: number
  updatedAt?: string
  status?: CreatorSeriesSummary['status']
}

export function CreatorSeries() {
  const [series, setSeries] = useState<SeriesRow[]>([])
  const [newTitle, setNewTitle] = useState('')
  const [newSynopsis, setNewSynopsis] = useState('')
  const [newGenre, setNewGenre] = useState('')
  const [newLanguage, setNewLanguage] = useState('en')
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listCreatorSeries()
      .then((items: CreatorSeriesSummary[]) => {
        if (cancelled) return
        setSeries(
          items.map((s) => ({
            id: s.id,
            title: s.title,
            visibility: (s.status === 'published' ? 'public' : 'draft') as SeriesRow['visibility'],
            episodes: s.episodeCount ?? 0,
            updatedAt: s.updatedAt,
            status: s.status,
          })),
        )
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load series')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim() || !newSynopsis.trim() || !newGenre.trim()) return
    setError(null)
    try {
      const created = await createSeries({
        title: newTitle.trim(),
        synopsis: newSynopsis.trim(),
        genre: newGenre.trim(),
        language: newLanguage || 'en',
        // For now, use a placeholder cover image key; in a full flow this would
        // come from an upload step before calling createSeries.
        coverImageKey: 'placeholder-cover-image.jpg',
      })
      setSeries((prev) => [
        {
          id: created.id,
          title: created.title,
          visibility: (created.status === 'published' ? 'public' : 'draft') as SeriesRow['visibility'],
          episodes: created.episodeCount ?? 0,
          updatedAt: created.updatedAt,
          status: created.status,
        },
        ...prev,
      ])
      setNewTitle('')
      setNewSynopsis('')
      setNewGenre('')
      setNewLanguage('en')
      setCreating(false)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create series')
    }
  }

  const toggleVisibility = (id: string) => {
    setSeries((prev) =>
      prev.map((s) =>
        s.id === id
          ? {
              ...s,
              visibility: s.visibility === 'public' ? 'unlisted' : 'public',
            }
          : s,
      ),
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Your series</h1>
          <p className="mt-1 text-sm text-gray-400">
            Create and manage your vertical drama series. This view now loads data directly from the
            Binyuma backend.
          </p>
        </div>
        <Button
          className="bg-[#D72638] hover:bg-[#D72638]/90 text-white"
          onClick={() => setCreating(true)}
        >
          New series
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-gray-800 bg-black/40 px-4 py-4 text-sm text-gray-400">
          Loading your series…
        </div>
      )}

      {creating && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-gray-800 bg-black/40 p-4 flex flex-col gap-3 md:flex-row md:items-end"
        >
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Series title
            </label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="e.g. City of Crowns"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Synopsis
            </label>
            <input
              type="text"
              value={newSynopsis}
              onChange={(e) => setNewSynopsis(e.target.value)}
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text_white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="Short description for this series"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Genre
            </label>
            <input
              type="text"
              value={newGenre}
              onChange={(e) => setNewGenre(e.target.value)}
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text_white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="e.g. Drama"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Language
            </label>
            <input
              type="text"
              value={newLanguage}
              onChange={(e) => setNewLanguage(e.target.value)}
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text_white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="en"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black">
              Create
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="border-gray-800 text-gray-300 hover:bg-white/5"
              onClick={() => {
                setCreating(false)
                setNewTitle('')
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>Series</div>
          <div>Visibility</div>
          <div>Episodes</div>
          <div className="text-right">Actions</div>
        </div>
        {!loading && series.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            You have no series yet. Click &quot;New series&quot; to start your first project.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {series.map((s) => (
              <div
                key={s.id}
                className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-3 text-sm items-center"
              >
                <div>
                  <Link
                    to={`/creator/series/${s.id}`}
                    className="font-medium text-white hover:text-[#D4AF37]"
                  >
                    {s.title}
                  </Link>
                  <p className="text-xs text-gray-500">
                    {s.updatedAt ? `Updated ${new Date(s.updatedAt).toLocaleDateString()}` : 'Never updated'}
                  </p>
                </div>
                <div className="flex flex-col gap-1 text-xs">
                  <span className="capitalize">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                        s.visibility === 'public'
                          ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700'
                          : s.visibility === 'unlisted'
                          ? 'bg-gray-900/40 text-gray-300 border border-gray-700'
                          : 'bg-yellow-900/30 text-yellow-200 border border-yellow-700'
                      }`}
                    >
                      {s.visibility}
                    </span>
                  </span>
                  {s.status && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                        s.status === 'approved'
                          ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700'
                          : s.status === 'pending_review'
                          ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/60'
                          : s.status === 'rejected'
                          ? 'bg-red-900/30 text-red-300 border border-red-700'
                          : 'bg-gray-900/40 text-gray-300 border border-gray-700'
                      }`}
                    >
                      {s.status === 'approved'
                        ? 'Published'
                        : s.status === 'pending_review'
                        ? 'Pending review'
                        : s.status === 'rejected'
                        ? 'Rejected'
                        : 'Draft'}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-300">{s.episodes}</div>
                <div className="flex justify-end gap-2">
                  <Button
                    size="xs"
                    variant="secondary"
                    className="border-gray-800 text-gray-300 hover:bg-white/5"
                    asChild
                  >
                    <Link to={`/creator/series/${s.id}`}>Manage episodes</Link>
                  </Button>
                  <Button
                    size="xs"
                    variant="secondary"
                    className="border-gray-800 text-gray-300 hover:bg-white/5"
                    asChild
                  >
                    <Link to={`/creator/series/${s.id}`}>Edit</Link>
                  </Button>
                  <Button
                    size="xs"
                    variant="secondary"
                    className="border-gray-800 text-gray-300 hover:bg-white/5"
                    onClick={() => toggleVisibility(s.id)}
                  >
                    {s.visibility === 'public' ? 'Make unlisted' : 'Make public'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        All changes here live only in your browser for now. When wired to the backend, creating or
        editing series will call real creator APIs in the Binyuma backend.
      </p>
    </div>
  )
}

